'use client';

import { useSearchParams } from 'next/navigation';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LocationType } from '@app/enums';
import { BoundingBox, createRectangle, MapTilingHandler, TileLabel } from '@app/helpers/map-tiling';
import { BoundsModel } from '@app/models';
import { GeoTileModel } from '@app/models/geo.tiles.model';
import { getGeoTiles } from '@app/state/api-thunks/map-accommodations.thunk';

import { useMapSearch } from './useMapSearch';

type UseMapDebugOverlayProps = {
    map: google.maps.Map | null;
    tiler: MapTilingHandler | null;
    tiles: BoundingBox[];
    zoom: number | null;
    isDebugMode?: boolean;
};

const BASE_DEBUG_CONFIG = {
    strokeOpacity: 0.75,
    strokeWeight: 1,
};

const DEBUG_CONFIG = {
    grid: {
        ...BASE_DEBUG_CONFIG,
        maxTiles: 100,
        strokeColor: '#FF0000',
        fillOpacity: 0,
    },
    geo: {
        ...BASE_DEBUG_CONFIG,
        strokeColor: '#5407fa',
        fillColor: '#5407fa',
        fillOpacity: 0.15,
    },
} as const;

export const useMapDebugOverlay = ({
    map,
    tiler,
    tiles,
    zoom,
    isDebugMode = false,
}: UseMapDebugOverlayProps) => {
    const searchParams = useSearchParams();
    const isOneAlgorithmMode = isDebugMode && searchParams?.get('oneAlgorithm') === '1';

    const indexVersion = useMemo(() => {
        if (!isDebugMode || !isOneAlgorithmMode) return undefined;

        const raw = searchParams?.get('indexVersion');
        const parsed = Number(raw);

        if (!isNaN(parsed) && Number.isInteger(parsed) && parsed > 0) {
            return parsed;
        }

        return undefined;
    }, [searchParams, isDebugMode, isOneAlgorithmMode]);

    const debugRectsRef = useRef<google.maps.Rectangle[]>([]);
    const debugLabelsRef = useRef<TileLabel[]>([]);
    const geoTilesRectsRef = useRef<google.maps.Rectangle[]>([]);

    const {
        searchCriteria: { geoId, locationType },
    } = useMapSearch();
    const [geoTiles, setGeoTiles] = useState<GeoTileModel[]>([]);
    const [geoTilesBounds, setGeoTilesBounds] = useState<BoundsModel[]>([]);

    const fetchGeoTiles = useCallback(
        async (geoId: string, locationType: LocationType) => {
            const tiles = await getGeoTiles(geoId, locationType, indexVersion);
            setGeoTiles(tiles || []);

            const bounds = (tiles || [])
                .map<BoundsModel | null>(tile => {
                    const [[lonMin, latMax], [lonMax, latMin]] = tile.bbox.coordinates;

                    if (latMin === latMax || lonMin === lonMax) return null;

                    return {
                        minLat: latMin,
                        maxLat: latMax,
                        minLon: lonMin,
                        maxLon: lonMax,
                    };
                })
                .filter((b): b is BoundsModel => Boolean(b));

            setGeoTilesBounds(bounds);
        },
        [indexVersion],
    );

    // Basic overlay - drawing tile grid rectangles
    useEffect(() => {
        if (!isDebugMode || !map || !tiler || !tiles.length || zoom === null) return;

        const targetZoom = tiler.clampLevel(zoom);
        const { maxTiles, ...rectOptions } = DEBUG_CONFIG.grid;

        tiles.slice(0, maxTiles).forEach((tile, index) => {
            const rect = createRectangle(
                {
                    north: tile.latMax,
                    south: tile.latMin,
                    east: tile.lonMax,
                    west: tile.lonMin,
                },
                {
                    ...rectOptions,
                    map,
                },
            );
            debugRectsRef.current.push(rect);

            const [x, y] = tiler.tileIndicesForLatLon(tile.latMax, tile.lonMin, targetZoom);
            const label = new TileLabel(index + 1, tile, { x, y, zoom });
            label.addToMap(map);
            debugLabelsRef.current.push(label);
        });

        return () => {
            debugRectsRef.current.forEach(rect => rect.setMap(null));
            debugRectsRef.current = [];

            debugLabelsRef.current.forEach(label => label.remove());
            debugLabelsRef.current = [];
        };
    }, [map, tiles, tiler, zoom, isDebugMode]);

    // One-algorithm overlay - drawing GeoTile rectangles
    useEffect(() => {
        if (!isOneAlgorithmMode || !map || !geoTiles.length) return;

        const { ...rectOptions } = DEBUG_CONFIG.geo;

        geoTiles.forEach(tile => {
            const [[lonMin, latMax], [lonMax, latMin]] = tile.bbox.coordinates;

            const rect = createRectangle(
                { north: latMax, south: latMin, east: lonMax, west: lonMin },
                { ...rectOptions, map },
            );
            geoTilesRectsRef.current.push(rect);
        });

        return () => {
            geoTilesRectsRef.current.forEach(rect => rect.setMap(null));
            geoTilesRectsRef.current = [];
        };
    }, [map, geoTiles, isOneAlgorithmMode]);

    useEffect(() => {
        if (!isOneAlgorithmMode || !geoId || !locationType) return;

        fetchGeoTiles(geoId, locationType);
    }, [fetchGeoTiles, isOneAlgorithmMode, geoId, locationType]);

    return { geoTilesBounds };
};
